/**
 * รายละเอียดลูกค้าหนึ่งราย — `GET /v1/customers/{customer_id}` (D-123 ข้อ 6)
 *
 * **เป็นหน้าจอย่อยด้วย search param ไม่ใช่ modal** แบบเดียวกับ `ProductMasterScreen`:
 * ที่อยู่หลายบรรทัดและผู้ติดต่อสามรายไม่พอดีกับกล่องที่ลอยทับตาราง และ URL ที่
 * bookmark/ส่งต่อได้คือสิ่งที่คนขายต้องการจริงเมื่อจะถามเพื่อนร่วมงานว่า "ลูกค้าราย
 * นี้ใช่รายที่คุณเพิ่งคุยไหม"
 *
 * **โหลดจาก `GET` รายเดียว ไม่ใช่จากแถวในตาราง** ทั้งที่ list ส่ง resource รูปเดียวกัน
 * มาแล้ว: แถวในมือเป็นภาพตอนที่โหลดรายการ และหน้านี้คือจุดที่ผู้ใช้มาอ่านที่อยู่เพื่อ
 * ส่งของจริง ข้อมูลที่เก่าไปหนึ่งการแก้ไขที่นี่แพงกว่าหนึ่งคำขอ
 *
 * ที่อยู่แสดงแบบ **คง line break** (`.cap-meta__value--multiline`) ไม่ใช่ยุบเป็นบรรทัด
 * เดียว — มันเป็นข้อความก้อนเดียวที่ผู้ใช้จัดบรรทัดไว้เอง (D-121) และที่อยู่ที่ถูกยุบ
 * คือที่อยู่ที่คนคัดลอกไปแปะบนใบส่งของแล้วอ่านไม่ออก
 *
 * ปุ่ม "แก้ไข" เป็นลิงก์พาไปหน้าแก้ไขของตัวเอง (`CustomerEditView`, `?view=edit&…`,
 * D-125) **ไม่ใช่กล่องลอยทับหน้านี้อีกต่อไป** — ที่อยู่หลายบรรทัดกับผู้ติดต่อสามราย
 * ไม่พอดีกับกล่องที่ลอยทับตารางเช่นเดียวกับที่หน้ารายละเอียดเองก็ไม่ใช่ modal
 *
 * หลังบันทึกสำเร็จที่หน้าแก้ไข ผู้ใช้ถูกนำทางกลับมาที่นี่พร้อม router state
 * `{ customerUpdated: true }` ซึ่งหน้านี้อ่านเพื่อแสดงข้อความสำเร็จที่เห็นได้ — ไม่ใช่
 * ข้อมูลที่หน้าจอเดาเอง เพราะ `fetchCustomer` ยังถูกเรียกใหม่ตามปกติทุกครั้งที่
 * component นี้ mount
 *
 * ⚠️ `CustomerFailureNotice` ถูก import มาจาก `CustomerScreen` ซึ่งเป็น **วงจร import
 * ระหว่างสองไฟล์นี้** (หน้าทะเบียน import หน้านี้เพื่อเรนเดอร์ drill-down) ตั้งใจและ
 * ปลอดภัย: ทั้งสองฝั่งอ้างกันด้วย `function` declaration ที่ถูก hoist ตอน instantiate
 * และถูกเรียกตอน render เท่านั้น ไม่ใช่ตอนที่ module body กำลังรัน ข้อความของความ
 * ล้มเหลวทุกสถานะอยู่ที่เดียวจึงดีกว่าสองชุดที่ต้องตามแก้ให้ตรงกัน
 */

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { CustomerFailureNotice } from './CustomerScreen';
import { InfoHint } from '../components/InfoHint';
import { StatusBadge } from '../StatusBadge';
import { fetchCustomer, type CustomerFailure } from '../../lib/customers/client';
import {
  customerStatusLabel,
  customerStatusTone,
} from '../../lib/customers/customersView';
import type { Customer, CustomerContact } from '../../lib/customers/types';

/** ค่าที่ยังไม่มี — ขีดเดียวกันทั้งหน้าจอ ไม่ใช่ช่องว่างที่อ่านเหมือนโหลดไม่ขึ้น */
const EMPTY_VALUE = '—';

export interface CustomerDetailViewProps {
  readonly customerId: string;
  /** สิทธิ์ `EDIT` ที่หน้าทะเบียนตรวจมาแล้ว — ปุ่ม "แก้ไข" ผูกกับค่านี้ */
  readonly canEdit: boolean;
  /**
   * query string ของรายการที่ผู้ใช้จากมา — พา sort/หน้า/พารามิเตอร์อื่นกลับไปครบ
   *
   * ส่งมาจากหน้าทะเบียน ไม่ใช่ประกอบขึ้นเองที่นี่: หน้านี้ไม่รู้ว่ารายการใช้
   * พารามิเตอร์อะไรอยู่ และการเดาจะทำให้ปุ่มกลับทิ้งตัวกรองของผู้ใช้เงียบ ๆ
   */
  readonly backSearch: string;
  /** query string ของหน้าแก้ไขของลูกค้ารายนี้ — ปุ่ม "แก้ไข" เป็นลิงก์ไปที่นี่ */
  readonly editSearch: string;
  /** เพิ่มค่าเมื่อทะเบียนถูกเขียน — หน้านี้โหลดซ้ำเพื่อให้สิ่งที่แสดงเป็นของจริง */
  readonly reloadToken: number;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

/** router state ที่หน้าแก้ไขส่งมาหลังบันทึกสำเร็จ (D-125) */
interface DetailLocationState {
  readonly customerUpdated?: boolean;
}

type DetailState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly customer: Customer }
  | CustomerFailure;

function contactValue(value: string | null): string {
  return value === null || value.trim() === '' ? EMPTY_VALUE : value;
}

export function CustomerDetailView({
  customerId,
  canEdit,
  backSearch,
  editSearch,
  reloadToken,
  apiBaseUrl,
  fetchImpl,
}: CustomerDetailViewProps) {
  const { pathname, state: locationState } = useLocation();
  const justSaved = Boolean((locationState as DetailLocationState | null)?.customerUpdated);
  const [retryToken, setRetryToken] = useState(0);
  const [state, setState] = useState<DetailState>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setState({ status: 'loading' });

    void fetchCustomer(customerId, {
      apiBaseUrl,
      fetchImpl,
      signal: controller.signal,
    }).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setState(result);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [customerId, apiBaseUrl, fetchImpl, reloadToken, retryToken]);

  const customer = state.status === 'ready' ? state.customer : null;
  const address = customer?.address ?? null;
  const contacts: readonly CustomerContact[] = customer?.contacts ?? [];

  return (
    <div className="cap-page" data-testid="customer-detail">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title" data-testid="customer-detail-code">
            {customer === null ? 'รายละเอียดลูกค้า' : customer.customer_code}
          </h1>
          <p className="cap-page__sub" data-testid="customer-detail-name">
            {customer === null ? `รหัสภายใน ${customerId}` : customer.customer_name}
          </p>
        </div>
        <div className="cap-page__actions">
          {customer === null ? null : (
            <StatusBadge tone={customerStatusTone(customer.status)}>
              {customerStatusLabel(customer.status)}
            </StatusBadge>
          )}
          {customer === null || !canEdit ? null : (
            <Link
              className="cap-button cap-button--primary"
              data-testid="customer-detail-edit-open"
              to={{ pathname, search: editSearch }}
            >
              แก้ไข
            </Link>
          )}
          <Link
            className="cap-button cap-button--ghost"
            data-testid="customer-detail-back"
            to={{ pathname, search: backSearch }}
          >
            กลับไปรายการ
          </Link>
        </div>
      </div>

      {justSaved && customer !== null ? (
        <div className="cap-notice" data-testid="customer-edit-result" role="status">
          <StatusBadge tone="success">บันทึกแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            บันทึกการแก้ไขลูกค้า {customer.customer_code} แล้ว
            <InfoHint
              label="เกี่ยวกับค่าที่แสดงหลังบันทึก"
              text="ค่าที่แสดงด้านล่างมาจากคำตอบของเซิร์ฟเวอร์หลังบันทึก ไม่ใช่จากสิ่งที่หน้าแก้ไขเพิ่งส่งไป"
            />
          </h2>
        </div>
      ) : null}

      {state.status === 'loading' ? (
        <div className="cap-notice" data-testid="customer-detail-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายละเอียดลูกค้า…</p>
        </div>
      ) : null}

      {state.status !== 'loading' && state.status !== 'ready' ? (
        <CustomerFailureNotice
          failure={state}
          context="detail"
          onRetry={() => {
            setRetryToken((token) => token + 1);
          }}
        />
      ) : null}

      {customer === null ? null : (
        <>
          <section className="cap-meta" aria-label="ข้อมูลลูกค้า">
            <div className="cap-meta__item">
              <div className="cap-meta__label">รหัสลูกค้า</div>
              <div className="cap-meta__value cap-table__code">
                {customer.customer_code}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">ชื่อลูกค้า</div>
              <div className="cap-meta__value">{customer.customer_name}</div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">สถานะ</div>
              <div className="cap-meta__value" data-testid="customer-detail-status">
                {customerStatusLabel(customer.status)}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">รหัสภายใน (customer_id)</div>
              <div className="cap-meta__value cap-table__code">{customer.customer_id}</div>
            </div>
          </section>

          <section className="cap-card cap-card--table" aria-label="ที่อยู่ลูกค้า">
            <div className="cap-card__head">
              <h2 className="cap-card__title">ที่อยู่</h2>
            </div>
            <div className="cap-meta cap-meta--flush">
              <div className="cap-meta__item">
                <div
                  className="cap-meta__value cap-meta__value--multiline"
                  data-testid="customer-detail-address"
                >
                  {address ?? EMPTY_VALUE}
                </div>
              </div>
            </div>
          </section>

          <section className="cap-card cap-card--table" aria-label="ผู้ติดต่อของลูกค้า">
            <div className="cap-card__head">
              <h2 className="cap-card__title">ผู้ติดต่อ</h2>
            </div>

            {contacts.length === 0 ? (
              <div className="cap-card__foot" data-testid="customer-detail-contacts-empty">
                ยังไม่มีผู้ติดต่อ
              </div>
            ) : (
              <div className="cap-table-wrap">
                <table className="cap-table" data-testid="customer-detail-contacts">
                  <caption className="cap-visually-hidden">
                    ผู้ติดต่อของลูกค้ารายนี้ เรียงตามหมายเลขช่องผู้ติดต่อ
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">ช่อง</th>
                      <th scope="col">ชื่อผู้ติดต่อ</th>
                      <th scope="col">โทรศัพท์</th>
                      <th scope="col">อีเมล</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((contact) => (
                      <tr
                        key={contact.contact_no}
                        data-testid={`customer-detail-contact-${contact.contact_no}`}
                      >
                        <th scope="row" className="cap-table__row-head">
                          {contact.contact_no}
                        </th>
                        <td>{contact.contact_name}</td>
                        <td>{contactValue(contact.phone)}</td>
                        <td>{contactValue(contact.email)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
